import React, { useEffect, useRef, useState } from 'react';
import { Mic, Square, Trash2, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';

const MAX_MS = 30000; // 30s hard cap
const TICK_MS = 100;

const fmt = (ms) => {
  const s = Math.floor(ms / 1000);
  const cs = Math.floor((ms % 1000) / 100);
  return `${String(s).padStart(2, '0')}.${cs}s`;
};

/**
 * VoiceMemoRecorder — coach-side. Records up to 30s of audio via MediaRecorder,
 * previews with <audio>, and POSTs multipart to /coach/locker/notes/voice.
 *
 * Props:
 *  - playerId (required)
 *  - roundReviewId (optional)
 *  - onSent(newNote) — parent refresh hook
 *  - label — button/heading label override
 */
const VoiceMemoRecorder = ({ playerId, roundReviewId = null, onSent, label = 'Voice memo' }) => {
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTsRef = useRef(0);
  const streamRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setSupported(false);
    }
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      stopStream();
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const pickMime = () => {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
    ];
    for (const m of candidates) {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) return m;
    }
    return '';
  };

  const start = async () => {
    if (!supported) {
      toast.error('Voice recording is not supported in this browser.');
      return;
    }
    setBlob(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    chunksRef.current = [];
    setElapsed(0);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMime();
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const type = mr.mimeType || 'audio/webm';
        const b = new Blob(chunksRef.current, { type });
        setBlob(b);
        setPreviewUrl(URL.createObjectURL(b));
        stopStream();
      };
      mr.start();
      startTsRef.current = Date.now();
      setRecording(true);
      timerRef.current = setInterval(() => {
        const ms = Date.now() - startTsRef.current;
        setElapsed(ms);
        if (ms >= MAX_MS) stop();
      }, TICK_MS);
    } catch (err) {
      toast.error(err?.message?.includes('denied') ? 'Microphone permission denied.' : 'Failed to start recording.');
    }
  };

  const stop = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch { /* noop */ }
    }
    setRecording(false);
  };

  const discard = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setBlob(null);
    setPreviewUrl(null);
    setElapsed(0);
    setNote('');
  };

  const send = async () => {
    if (!blob) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append('player_id', playerId);
      if (roundReviewId) form.append('round_review_id', roundReviewId);
      form.append('duration_ms', String(Math.min(elapsed, MAX_MS)));
      if (note.trim()) form.append('note', note.trim());
      const type = blob.type.split(';')[0] || 'audio/webm';
      const ext = type.includes('mp4') ? 'm4a' : type.includes('mpeg') ? 'mp3' : type.includes('ogg') ? 'ogg' : type.includes('wav') ? 'wav' : 'webm';
      const filename = `memo-${Date.now()}.${ext}`;
      form.append('file', new File([blob], filename, { type }));
      const { data } = await API.post('/coach/locker/notes/voice', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Voice memo sent to player.');
      discard();
      onSent && onSent(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send voice memo.');
    } finally {
      setSending(false);
    }
  };

  if (!supported) {
    return (
      <div className="text-xs text-[#A8A29E] italic" data-testid="voice-recorder-unsupported">
        Voice memos aren't supported in this browser.
      </div>
    );
  }

  return (
    <div className="border border-dashed border-[#BC562B]/40 bg-[#FFF9F5] p-3" data-testid={`voice-recorder-${playerId}${roundReviewId ? '-' + roundReviewId : ''}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Mic className="w-4 h-4 text-[#BC562B]" />
          <p className="text-xs font-semibold uppercase tracking-wider text-[#BC562B]">{label} · up to 30s</p>
        </div>
        <div className="flex items-center gap-2">
          {!recording && !blob && (
            <button
              type="button"
              onClick={start}
              className="inline-flex items-center gap-1.5 bg-[#BC562B] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#9A4520]"
              data-testid="voice-record-start"
            >
              <Mic className="w-3.5 h-3.5" /> Record
            </button>
          )}
          {recording && (
            <>
              <span className="inline-flex items-center gap-1.5 text-xs text-[#BC562B] tabular-nums font-semibold" data-testid="voice-elapsed">
                <span className="w-2 h-2 rounded-full bg-[#BC562B] animate-pulse" /> {fmt(elapsed)} / 30.0s
              </span>
              <button
                type="button"
                onClick={stop}
                className="inline-flex items-center gap-1.5 bg-[#1A2E1D] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#0F1F12]"
                data-testid="voice-record-stop"
              >
                <Square className="w-3.5 h-3.5" /> Stop
              </button>
            </>
          )}
        </div>
      </div>

      {blob && previewUrl && (
        <div className="mt-3 space-y-2" data-testid="voice-preview">
          <audio src={previewUrl} controls className="w-full h-9" data-testid="voice-preview-audio" />
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional caption (e.g. Focus for Sunday's round)"
            maxLength={2000}
            className="w-full text-xs bg-white border border-[#E7E5E4] px-2 py-1.5 focus:outline-none focus:border-[#1A2E1D]"
            data-testid="voice-caption-input"
          />
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={discard}
              disabled={sending}
              className="inline-flex items-center gap-1.5 text-xs text-[#737373] hover:text-[#1A1A1A] px-2 py-1.5"
              data-testid="voice-discard"
            >
              <Trash2 className="w-3.5 h-3.5" /> Discard
            </button>
            <button
              type="button"
              onClick={send}
              disabled={sending}
              className="inline-flex items-center gap-1.5 bg-[#2C5530] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#1E3A21] disabled:opacity-50"
              data-testid="voice-send"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {sending ? 'Sending…' : 'Send memo'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceMemoRecorder;
